import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useTheme } from "@/context/ThemeContext";
import { CURRENCIES } from "@/lib/format";
import { APP_NAME, LOGO_MARK } from "@/lib/brand";
import { NAV_GROUPS } from "@/lib/nav";
import { CommandPalette } from "@/components/CommandPalette";
import {
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const COLLAPSE_KEY = "ethara-sidebar-collapsed";

function Brand({ compact }) {
  const { theme } = useTheme();
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div
        className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center overflow-hidden ring-1 ring-brand/30 shadow-glow-cyan ${
          theme === "light" ? "bg-slate-900" : "bg-foreground/[0.03]"
        }`}
      >
        <img src={LOGO_MARK} alt={APP_NAME} className="h-6 w-6 object-contain" />
      </div>
      {!compact && (
        <div className="min-w-0">
          <p className="font-heading font-bold text-[15px] leading-none tracking-tight text-foreground">
            Ethara<span className="text-brand text-glow-cyan">.AI</span>
          </p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-1 truncate">
            Finance · MIS
          </p>
        </div>
      )}
    </div>
  );
}

function SidebarNav({ unmatched, exceptions, pendingBills, collapsed, onNavigate }) {
  const badgeFor = (id) =>
    id === "recon" ? unmatched : id === "inbox" ? exceptions : id === "invoice-inbox" ? pendingBills : 0;

  return (
    <nav className="flex-1 px-2 py-4 overflow-y-auto overflow-x-hidden" data-testid="sidebar-nav">
      {NAV_GROUPS.map((group) => (
        <div key={group.label} className="mb-4 last:mb-2">
          {collapsed ? (
            <div className="mx-2 mb-2 h-px bg-border" />
          ) : (
            <p className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/60">
              {group.label}
            </p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const badge = badgeFor(item.id);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.id}`}
                  className={({ isActive }) =>
                    `relative flex items-center rounded-md text-sm transition-colors ${
                      collapsed ? "justify-center h-10 w-10 mx-auto" : "gap-3 px-3 py-2"
                    } ${
                      isActive
                        ? "bg-brand/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && !collapsed && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-brand" />
                      )}
                      <item.icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {badge > 0 &&
                        (collapsed ? (
                          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400" />
                        ) : (
                          <span
                            data-testid={item.id === "recon" ? "nav-recon-badge" : "nav-inbox-badge"}
                            className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30"
                          >
                            {badge}
                          </span>
                        ))}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function UserChip({ user, collapsed }) {
  const initial = (user?.name || user?.email || "U").charAt(0);
  return (
    <div className="px-3 py-4 border-t border-border">
      <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
        <div
          title={collapsed ? `${user?.name || "User"} · ${user?.role || ""}` : undefined}
          className="h-9 w-9 shrink-0 rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center text-sm font-semibold text-brand uppercase"
        >
          {initial}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="user-name">
                {user?.name || "User"}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            {user?.role && (
              <span
                data-testid="user-role"
                className="shrink-0 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-brand/10 text-brand border border-brand/25 capitalize"
              >
                {user.role}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { currency, setCurrency, rateMode, setRateMode, rateAsOf, setRateAsOf } = useCurrency();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [unmatched, setUnmatched] = useState(0);
  const [exceptions, setExceptions] = useState(0);
  const [pendingBills, setPendingBills] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setRefreshing(false), 900);
  };

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    let alive = true;
    const poll = () => {
      api
        .get("/recon/summary")
        .then(({ data }) => alive && setUnmatched(data.unmatched || 0))
        .catch(() => {});
      api
        .get("/exceptions")
        .then(({ data }) => alive && setExceptions(data.items?.length || 0))
        .catch(() => {});
      api
        .get("/google-drive/status")
        .then(({ data }) => alive && setPendingBills(data.pending || 0))
        .catch(() => {});
    };
    poll();
    const t = setInterval(poll, 90000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [location.pathname]);

  useEffect(() => {
    setDrawer(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left sidebar — the only navigation */}
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`hidden lg:flex ${
          collapsed ? "w-[68px]" : "w-64"
        } shrink-0 flex-col border-r border-border bg-background/70 backdrop-blur-xl sticky top-0 h-screen z-10 transition-[width] duration-200`}
      >
        <div className={`py-5 border-b border-border flex items-center ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          <Brand compact={collapsed} />
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              data-testid="sidebar-collapse-btn"
              title="Collapse menu"
              className="text-muted-foreground hover:text-foreground"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            data-testid="sidebar-expand-btn"
            title="Expand menu"
            className="mx-auto mt-3 h-9 w-9 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05]"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}
        <SidebarNav unmatched={unmatched} exceptions={exceptions} pendingBills={pendingBills} collapsed={collapsed} />
        <UserChip user={user} collapsed={collapsed} />
      </aside>

      {/* Mobile / tablet drawer — same left menu */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="nav-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <aside className="relative w-[270px] flex flex-col border-r border-border bg-background h-full">
            <div className="px-4 py-5 border-b border-border flex items-center justify-between">
              <Brand />
              <button
                onClick={() => setDrawer(false)}
                data-testid="nav-drawer-close"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarNav
              unmatched={unmatched}
              exceptions={exceptions}
              pendingBills={pendingBills}
              collapsed={false}
              onNavigate={() => setDrawer(false)}
            />
            <UserChip user={user} collapsed={false} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 border-b border-border bg-background/80 backdrop-blur-xl backdrop-saturate-150 flex items-center justify-between gap-3 px-4 md:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setDrawer(true)}
              data-testid="nav-drawer-toggle"
              className="lg:hidden h-10 w-10 shrink-0 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="lg:hidden min-w-0">
              <Brand compact />
            </div>
            <button
              onClick={() => navigate(-1)}
              data-testid="global-back-btn"
              title="Go back"
              aria-label="Go back"
              className="h-10 w-10 shrink-0 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="global-refresh-btn"
              title="Refresh data"
              aria-label="Refresh data"
              className="h-10 w-10 shrink-0 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-colors disabled:opacity-80"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin text-brand" : ""}`} />
            </button>
            <p className="hidden lg:block text-xs tracking-[0.16em] uppercase text-muted-foreground">
              Financial Management &amp; Investor MIS
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <CommandPalette />
            <Select value={rateMode} onValueChange={setRateMode}>
              <SelectTrigger className="w-[112px] hidden md:flex" data-testid="rate-mode-toggle" title="FX rate mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="average" data-testid="rate-mode-average">Average</SelectItem>
                <SelectItem value="closing" data-testid="rate-mode-closing">Closing</SelectItem>
                <SelectItem value="spot" data-testid="rate-mode-spot">Spot</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="date"
              value={rateAsOf}
              onChange={(e) => setRateAsOf(e.target.value)}
              data-testid="rate-as-of"
              title="Rate as-of date (leave blank for latest)"
              className="h-10 rounded-md bg-background border border-input px-2 text-xs w-[138px] hidden xl:block"
            />
            {CURRENCIES.length > 1 && (
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-[104px]" data-testid="currency-toggle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>
                      {c === "INR" ? "₹ INR" : "$ USD"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className="h-10 w-10 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-brand/40 transition-colors"
            >
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        {refreshing && (
          <div className="h-0.5 w-full bg-brand animate-pulse" data-testid="refresh-indicator" />
        )}

        <main className="flex-1 p-4 md:p-8 max-w-[1500px] w-full mx-auto">
          <div key={refreshKey}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
